import { useMemo } from 'react';
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query';
import { queryFnFetchErrorWrapper } from '../utils/errorHandlers';
import { FetchError } from 'src/types/TApiQueries';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { queryNames } from 'src/api/utils/queryNames';
import {
  renameConnectedApp,
  signOutAllConnectedApps,
  signOutConnectedApp,
} from 'src/api/functions/connectedAppsApi';
import type { SignOutAllResult } from 'src/types/ConnectedApps';

type SignOutVars = { id: number; includeSchedules: boolean };
type SignOutAllVars = { userId?: number; includeSchedules: boolean };
type RenameVars = { id: number; label: string };

/**
 * Every connected-apps view (Profile, a member's modal, the account-wide
 * modal, the Users-table counts) reads under one key prefix, so one
 * invalidation refreshes them all. Signing out the scheduler's session also
 * drops the stored scheduler credential, so the schedules views refresh too.
 */
function useInvalidateConnectedApps() {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries([queryNames.connectedApps]);
    await queryClient.invalidateQueries([queryNames.scheduleCredentials]);
  };
}

export function useSignOutConnectedAppMutation(
  mutationOptions?: UseMutationOptions<void, FetchError, SignOutVars>,
) {
  const invalidate = useInvalidateConnectedApps();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () =>
      queryFnFetchErrorWrapper(({ id, includeSchedules }: SignOutVars) =>
        signOutConnectedApp(accessToken, id, includeSchedules),
      ),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useSignOutAllConnectedAppsMutation(
  mutationOptions?: UseMutationOptions<SignOutAllResult, FetchError, SignOutAllVars>,
) {
  const invalidate = useInvalidateConnectedApps();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () =>
      queryFnFetchErrorWrapper(({ userId, includeSchedules }: SignOutAllVars) =>
        signOutAllConnectedApps(accessToken, userId, includeSchedules),
      ),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}

export function useRenameConnectedAppMutation(
  mutationOptions?: UseMutationOptions<unknown, FetchError, RenameVars>,
) {
  const invalidate = useInvalidateConnectedApps();
  const accessToken = useAccessToken();

  const mutationFn = useMemo(
    () => queryFnFetchErrorWrapper(({ id, label }: RenameVars) => renameConnectedApp(accessToken, id, label)),
    [accessToken],
  );

  return useMutation(mutationFn, {
    ...mutationOptions,
    onSuccess: async (result, ...args) => {
      await invalidate();
      mutationOptions?.onSuccess?.(result, ...args);
    },
  });
}
